import { tags } from "@/components/Forms/AddNoteDialog.tsx";
import { completeTaskSchema } from "@/components/Forms/schema/completeTaskSchema.ts";
import RhfDocumentsUpload, {
    type RhfDocumentsUploadRef,
} from "@/components/RhfDocumentsUpload.tsx";
import { useCompleteTask } from "@/mutations/useCompleteTask.tsx";
import { useCompleteTaskWithDocuments } from "@/mutations/useCompleteTaskWithDocuments.ts";
import type { CaseTask } from "@/types/caseTask.ts";
import type { DocumentData } from "@/types/document.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid2 as Grid,
    MenuItem,
} from "@mui/material";
import { RhfTextField } from "mui-rhf-integration";
import { enqueueSnackbar } from "notistack";
import { type ReactNode, useCallback, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import type { z } from "zod";

export type CompleteTaskFormValues = z.infer<typeof completeTaskSchema>;

type Props = {
    caseTaskUUID: string;
    taskName: string;
    dueDate: Date;
    description: string;
    caseStatus: string;
    caseUUID: string;
    caseTask: CaseTask;
};

const CompleteTaskForCaseButton = ({
    caseTaskUUID,
    taskName,
    dueDate,
    description,
    caseStatus,
    caseUUID,
    caseTask,
}: Props): ReactNode => {
    const [open, setOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const completeTask = useCompleteTask();
    const createDocumentsMutation = useCompleteTaskWithDocuments();
    const documentsUploadRef = useRef<RhfDocumentsUploadRef>(null);

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setOpen(true);
    };

    const form = useForm<CompleteTaskFormValues>({
        resolver: zodResolver(completeTaskSchema),
        defaultValues: {
            note: "",
            tag: caseTask.defaultNoteTag ?? "",
        },
    });

    const closeDialog = useCallback(() => {
        setOpen(false);
        setIsSubmitting(false);
        form.reset();
    }, [form]);

    const handleSubmit = useCallback(
        async (values: CompleteTaskFormValues, type: string) => {
            if (isSubmitting) {
                return;
            }

            setIsSubmitting(true);
            let uploadedDocuments: DocumentData[] = [];

            try {
                if (documentsUploadRef.current?.hasDocuments()) {
                    uploadedDocuments = await documentsUploadRef.current.uploadDocuments(
                        values.tag,
                    );
                }
            } catch (error) {
                enqueueSnackbar("Document upload failed", { variant: "error" });
                setIsSubmitting(false);
                return;
            }

            if (uploadedDocuments.length > 0) {
                // Has documents - use createDocumentsMutation
                createDocumentsMutation.mutate(
                    {
                        caseTaskUUID: caseTaskUUID,
                        saveType: type,
                        documents: uploadedDocuments,
                        formValues: values,
                    },
                    {
                        onSuccess: () => {
                            enqueueSnackbar(
                                `Task ${
                                    type === "complete" ? "completed" : "saved"
                                } and documents uploaded`,
                                { variant: "success" },
                            );
                            closeDialog();
                        },
                        onError: () => {
                            enqueueSnackbar("Completing task with documents failed", {
                                variant: "error",
                            });
                            setIsSubmitting(false);
                        },
                    },
                );
            } else {
                // No documents - use completeTask
                completeTask.mutate(
                    {
                        idUUID: caseTaskUUID,
                        saveType: type,
                        caseStatus,
                        ...values,
                    },
                    {
                        onSuccess: () => {
                            enqueueSnackbar(`Task ${type === "complete" ? "completed" : "saved"}`, {
                                variant: "success",
                            });
                            closeDialog();
                        },
                        onError: () => {
                            enqueueSnackbar("Completing task failed", { variant: "error" });
                            setIsSubmitting(false);
                        },
                    },
                );
            }
        },
        [
            caseTaskUUID,
            caseStatus,
            closeDialog,
            completeTask,
            createDocumentsMutation,
            isSubmitting,
        ],
    );

    const handleCompleteAndClose = () => {
        form.trigger().then((isValid) => {
            if (isValid) {
                handleSubmit(form.getValues(), "complete");
            }
        });
    };

    const handleSaveAndClose = () => {
        form.trigger().then((isValid) => {
            if (isValid) {
                handleSubmit(form.getValues(), "save");
            }
        });
    };

    return (
        <>
            <Button variant={"contained"} onClick={handleClick}>
                Complete
            </Button>
            <Dialog open={open} maxWidth="sm" fullWidth>
                <FormProvider {...form}>
                    <form>
                        <DialogTitle>Complete Task</DialogTitle>
                        <DialogContent dividers>
                            <Grid container spacing={2}>
                                <Grid size={6} sx={{ p: 1 }}>
                                    Name: {taskName}
                                </Grid>
                                <Grid size={6} sx={{ p: 1 }}>
                                    Due:{" "}
                                    {caseTask.duration === 0 ? "N/A" : dueDate.toLocaleDateString()}
                                </Grid>
                                <Grid size={12} sx={{ p: 1 }}>
                                    Description: {description}
                                </Grid>
                                <Grid size={12} sx={{ p: 1 }}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"note"}
                                        label={"Note"}
                                        multiline
                                        rows={4}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={12} sx={{ p: 1 }}>
                                    <RhfTextField
                                        control={form.control}
                                        select
                                        name={"tag"}
                                        label={"Tag"}
                                        sx={{
                                            minWidth: "355px",
                                            width: "100%",
                                            mb: 2,
                                        }}
                                    >
                                        <MenuItem key={"tag"} value={""}>
                                            Blank
                                        </MenuItem>
                                        {tags.map((tag) => (
                                            <MenuItem key={tag} value={tag}>
                                                {tag}
                                            </MenuItem>
                                        ))}
                                    </RhfTextField>
                                </Grid>
                                <Grid size={12} sx={{ p: 1 }}>
                                    <RhfDocumentsUpload
                                        ref={documentsUploadRef}
                                        caseUUID={caseUUID}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeDialog} disabled={isSubmitting}>
                                Close
                            </Button>
                            <Button
                                variant={"contained"}
                                onClick={handleSaveAndClose}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "Saving..." : "Save & Close"}
                            </Button>
                            <Button
                                variant={"contained"}
                                onClick={handleCompleteAndClose}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "Completing..." : "Complete Task"}
                            </Button>
                        </DialogActions>
                    </form>
                </FormProvider>
            </Dialog>
        </>
    );
};

export default CompleteTaskForCaseButton;
